import { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  HStack,
  Avatar,
  Text,
  Image,
  Button,
  VStack,
  IconButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  useToast,
  Badge,
} from '@chakra-ui/react';
import { MessageCircle, Share2, MoreHorizontal, Flag, MapPin, List, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from '@tanstack/react-router';
import { useOpenRestaurantProfile } from '@/hooks/useOpenRestaurantProfile';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useReactToPost, useUnreactToPost, useSharePost } from '@/api';
import type { Post as PostType } from '@/api';
import type { ReactionType, ReactionCounts, ReactedByUser, Poll } from '@/types/social';
import { Comments } from './Comments';
import { RenderContentWithMentions, ReportModal } from '@/components/social';
import { LikesModal } from '@/components/social/LikesModal';
import { ReactionBar } from './reactions';
import { PollCard } from './polls';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from '@/store/slices/authSlice';

// Extended post type to include poll
interface PostWithPoll extends PostType {
  poll?: Poll | null;
}

interface FeedPostProps {
  post: PostWithPoll;
  initialCommentsOpen?: boolean;
  onPostClick?: (urlKey: string, type: 'post') => void;
  onModalClose?: () => void;
}

export const FeedPost = memo(function FeedPost({ post, initialCommentsOpen = false, onPostClick, onModalClose }: FeedPostProps) {
  // Reaction state (replaces likes)
  const [userReaction, setUserReaction] = useState<ReactionType | null>(post?.userReaction ?? null);
  const [reactionCounts, setReactionCounts] = useState<ReactionCounts>(post?.reactionCounts ?? {});
  const [totalReactions, setTotalReactions] = useState(post?.totalReactions ?? 0);
  const [reactedBy, setReactedBy] = useState<ReactedByUser[]>(post?.reactedBy ?? []);

  // Legacy state for backward compatibility
  const [shareCount, setShareCount] = useState(post?.shareCount ?? 0);
  const [commentsOpen, setCommentsOpen] = useState(initialCommentsOpen);
  const { isOpen: isReportModalOpen, onOpen: onReportModalOpen, onClose: onReportModalClose } = useDisclosure();
  const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onLikesModalClose } = useDisclosure();

  const { mutateAsync: reactToPost, isPending: isReacting } = useReactToPost();
  const { mutateAsync: unreactToPost, isPending: isUnreacting } = useUnreactToPost();
  const { mutateAsync: sharePost } = useSharePost();
  const toast = useToast();
  const navigate = useNavigate();
  const openRestaurantProfile = useOpenRestaurantProfile();
  const isLoggedIn = useSelector(selectIsLoggedIn);

  // Auto-open comments if URL has a comment hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#comment-') && !commentsOpen) {
      setCommentsOpen(true);
    }
  }, []);

  // Sync local state with props when post data changes (e.g., background refetch)
  useEffect(() => {
    if (post) {
      setUserReaction(post.userReaction ?? null);
      setReactionCounts(post.reactionCounts ?? {});
      setTotalReactions(post.totalReactions ?? 0);
      setReactedBy(post.reactedBy ?? []);
    }
  }, [post?.userReaction, post?.reactionCounts, post?.totalReactions, post?.reactedBy]);

  // Guard against null/undefined post
  if (!post) {
    return null;
  }

  // Handle adding/changing a reaction
  const handleReact = useCallback(
    async (reaction: ReactionType) => {
      const previousReaction = userReaction;
      const previousCounts = { ...reactionCounts };
      const previousTotal = totalReactions;

      try {
        // Optimistically update UI
        if (previousReaction) {
          // Changing reaction: decrement old, increment new
          setReactionCounts((prev) => ({
            ...prev,
            [previousReaction]: Math.max(0, (prev[previousReaction] || 0) - 1),
            [reaction]: (prev[reaction] || 0) + 1,
          }));
        } else {
          // New reaction
          setReactionCounts((prev) => ({
            ...prev,
            [reaction]: (prev[reaction] || 0) + 1,
          }));
          setTotalReactions((prev) => prev + 1);
        }
        setUserReaction(reaction);

        // API call
        await reactToPost({ urlKey: post.urlKey, reactionType: reaction });
      } catch (error) {
        // Revert on error
        setUserReaction(previousReaction);
        setReactionCounts(previousCounts);
        setTotalReactions(previousTotal);
        console.error('Failed to react to post:', error);
      }
    },
    [userReaction, reactionCounts, totalReactions, post.urlKey, reactToPost]
  );

  // Handle removing a reaction
  const handleRemoveReaction = useCallback(async () => {
    if (!userReaction) return;

    const previousReaction = userReaction;
    const previousCounts = { ...reactionCounts };
    const previousTotal = totalReactions;

    try {
      // Optimistically update UI
      setReactionCounts((prev) => ({
        ...prev,
        [previousReaction]: Math.max(0, (prev[previousReaction] || 0) - 1),
      }));
      setTotalReactions((prev) => Math.max(0, prev - 1));
      setUserReaction(null);

      // API call
      await unreactToPost(post.urlKey);
    } catch (error) {
      // Revert on error
      setUserReaction(previousReaction);
      setReactionCounts(previousCounts);
      setTotalReactions(previousTotal);
      console.error('Failed to remove reaction:', error);
    }
  }, [userReaction, reactionCounts, totalReactions, post.urlKey, unreactToPost]);

  // Share post handler — uses @capacitor/share on native iOS/Android, falls back to
  // web navigator.share, then clipboard copy in browser dev mode.
  //
  // The share URL points at the server-rendered OG meta page served by Django.
  // Deployment prerequisite: VITE_API_URL must be set to the public API base URL
  // (e.g. https://api.paire.app) so that shared links resolve correctly and
  // produce rich link previews in iMessage, WhatsApp, etc.
  const handleShare = useCallback(async () => {
    // Build the OG share URL — Django serves this view at GET /share/posts/<id>/
    const shareBaseUrl = import.meta.env.VITE_API_URL ?? '';
    const shareUrl = `${shareBaseUrl}/share/posts/${post.id}/`;

    const shareTitle = `${post.user ? [post.user.firstName, post.user.lastName].filter(Boolean).join(' ') || 'Someone' : 'Someone'} on Paire`;
    const shareText = post.content ? post.content.substring(0, 100) : '';

    try {
      // On native platforms, invoke the OS share sheet via the Capacitor Share plugin
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
          dialogTitle: 'Share this post',
        });
      } else if ('share' in navigator) {
        // Web: try the Web Share API (works in Chrome/Safari PWA contexts)
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } else {
        // Final fallback: copy link to clipboard
        // Use window.navigator to avoid TypeScript narrowing navigator to `never` after the `in` check
        await window.navigator.clipboard.writeText(shareUrl);
        toast({
          title: 'Link copied!',
          description: 'Post link copied to clipboard.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

      // Track the share on backend (only if logged in); update local count only on success
      if (isLoggedIn) {
        try {
          await sharePost(post.id);
          setShareCount((prev) => prev + 1);
        } catch (shareError) {
          // Share tracking failed — count stays unchanged, no user-facing error needed
          console.error('Failed to track share:', shareError);
        }
      }
    } catch (error) {
      // AbortError means the user dismissed the share sheet — not worth surfacing
      if (error instanceof Error && error.name !== 'AbortError') {
        toast({
          title: 'Failed to share post',
          status: 'error',
          duration: 2000,
        });
        console.error('Failed to share post:', error);
      }
    }
  }, [post.id, post.user?.firstName, post.user?.lastName, post.content, isLoggedIn, sharePost, toast]);

  const handleCommentClick = () => {
    // Require authentication for commenting
    if (!isLoggedIn) {
      navigate({ to: '/login' });
      return;
    }
    setCommentsOpen(!commentsOpen);
  };

  const handlePostClick = (e: React.MouseEvent) => {
    // Don't navigate if user clicked on a button, link, or interactive element
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]') ||
      target.closest('textarea') ||
      target.closest('input')
    ) {
      return;
    }

    // Use custom handler if provided, otherwise navigate to feed
    if (onPostClick) {
      onPostClick(post.urlKey, 'post');
    } else {
      // Navigate to feed with post query param to open modal (fallback)
      navigate({ to: '/feed', search: { post: post.urlKey } });
    }
  };

  return (
    <Box
      bg="var(--card-bg)"
      color="var(--card-text)"
      borderRadius="var(--r-card)"
      border="1px solid"
      borderColor="var(--color-overlay-12)"
      boxShadow="0 1px 3px var(--color-overlay-08)"
      overflow="hidden"
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      cursor="pointer"
      onClick={handlePostClick}
      _hover={{
        boxShadow: '0 8px 24px var(--color-overlay-12)',
        transform: 'translateY(-4px)',
        borderColor: 'var(--color-purple-glow-20)',
      }}
    >
      <Box p={{ base: 3, lg: 4 }}>
        <Flex justify="space-between" align="center" mb={{ base: 2, lg: 3 }}>
          <HStack spacing={{ base: 2, lg: 3 }}>
            <Avatar
              size={{ base: 'sm', lg: 'md' }}
              name={post.user?.name ?? 'Member'}
              src={post.user?.photoUrl || undefined}
              // D66: Chakra's default Avatar fallback (no bg/color set) picks a
              // random per-name background via randomColor() and a binary
              // white/gray.800 text color from isDark() (@chakra-ui/theme
              // avatar.mjs) — a coarse light/dark heuristic that isn't a real
              // WCAG contrast guarantee, so some name hashes fail 4.5:1
              // (confirmed via the D66 sweep: chakra-avatar__initials nodes on
              // /posts/$urlKey, /collaborative lists, /onboarding/profile).
              // Pinning a fixed, deterministic, already-passing card-token
              // pair removes the randomness entirely for the initials
              // fallback; it has no effect once a real photoUrl loads.
              bg="var(--card-surface-2)"
              color="var(--card-text)"
              cursor="pointer"
              _hover={{ opacity: 0.8 }}
              onClick={() => post.user?.id && navigate({ to: '/profile/$userId', params: { userId: post.user.id } })}
            />
            <VStack align="start" spacing={0}>
              <Text
                fontWeight="bold"
                fontSize={{ base: 'sm', lg: 'md' }}
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => post.user?.id && navigate({ to: '/profile/$userId', params: { userId: post.user.id } })}
              >
                {post.user?.name ?? 'Member'}
              </Text>
              {/* D66: --card-text-3 (#8A8A8A light) is ~3.45:1 against --card-bg
                  white — fails WCAG's 4.5:1 normal-text floor even at the
                  token's pure value, independent of any surface it sits on.
                  --card-text-2 clears 4.5:1 in both modes. */}
              <Text fontSize="xs" color="var(--card-text-2)">
                {formatDistanceToNow(new Date(post.createdAt ?? new Date()), { addSuffix: true })}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={1}>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="More options"
                icon={<MoreHorizontal size={20} />}
                variant="ghost"
                size={{ base: 'xs', lg: 'sm' }}
              />
              <MenuList>
                <MenuItem icon={<Flag size={16} />} onClick={onReportModalOpen}>
                  Report Post
                </MenuItem>
              </MenuList>
            </Menu>
            {onModalClose && (
              <IconButton
                aria-label="Close"
                icon={<X size={20} />}
                variant="ghost"
                size={{ base: 'xs', lg: 'sm' }}
                onClick={onModalClose}
              />
            )}
          </HStack>
        </Flex>
        <Box mb={{ base: 2, lg: 3 }}>
          {/* D66: RenderContentWithMentions defaults `color` to a literal
              'black' when the caller doesn't pass one — invisible
              (~1.3:1) against --card-bg's dark-mode value. Pass the card
              text token explicitly rather than editing the shared
              component's default (out of this fix's file scope). */}
          <RenderContentWithMentions
            content={post.content ?? ''}
            mentions={post.mentions ?? []}
            restaurants={post.restaurants ?? []}
            lists={post.lists ?? []}
            color="var(--card-text)"
          />
        </Box>

        {/* Restaurant and List Tags */}
        {((post.restaurants && post.restaurants.length > 0) ||
          (post.lists && Object.keys(post.lists).length > 0)) && (
          <HStack spacing={2} mb={{ base: 2, lg: 3 }} flexWrap="wrap">
            {post.restaurants &&
              post.restaurants.map((restaurant) => (
                <Badge
                  key={restaurant.id}
                  bg="var(--color-primary-purple)"
                  color="white"
                  px={{ base: 1.5, lg: 2 }}
                  py={{ base: 0.5, lg: 1 }}
                  borderRadius="var(--r-chip)"
                  cursor="pointer"
                  transition="all 0.2s"
                  fontSize={{ base: 'xs', lg: 'sm' }}
                  _hover={{ bg: 'var(--color-purple-600)', transform: 'scale(1.05)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Post badges only carry id + name: warm chunk, cold data path.
                    openRestaurantProfile(restaurant.id);
                  }}
                >
                  <HStack spacing={1}>
                    <MapPin size={12} />
                    <Text fontSize={{ base: 'xs', lg: 'sm' }}>{restaurant.name}</Text>
                  </HStack>
                </Badge>
              ))}
            {post.lists &&
              post.lists.map((list) => (
                <Badge
                  key={list.urlKey}
                  bg="var(--color-secondary-green)"
                  // TEXT-COLOR-LITERAL: fixed-brand badge text color, non-mode-paired, contrast-verified 9.19:1 (blackAlpha.800)
                  // D66: white on this brand green is ~1.6:1 — fails WCAG
                  // even in theory, not surface-dependent. blackAlpha.800 is
                  // 9.19:1; kept as a literal (not a --card-text* token)
                  // since this bg is a fixed brand color, not mode-paired —
                  // a mode-flipping text token would fail in dark mode.
                  color="blackAlpha.800"
                  px={{ base: 1.5, lg: 2 }}
                  py={{ base: 0.5, lg: 1 }}
                  borderRadius="var(--r-chip)"
                  cursor="pointer"
                  transition="all 0.2s"
                  fontSize={{ base: 'xs', lg: 'sm' }}
                  _hover={{ bg: 'var(--color-secondary-green)', transform: 'scale(1.05)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({ to: '/lists/$urlKey', params: { urlKey: list.urlKey } });
                  }}
                >
                  <HStack spacing={1}>
                    <List size={12} />
                    <Text fontSize={{ base: 'xs', lg: 'sm' }}>{list.name}</Text>
                  </HStack>
                </Badge>
              ))}
          </HStack>
        )}
      </Box>

      {/* Support both new photoUrls and legacy images */}
      {((post.photoUrls && post.photoUrls.length > 0) || (post.images && post.images.length > 0)) && (
        <Image
          src={post.photoUrls?.[0] || post.images?.[0]?.image}
          alt="Post image"
          w="100%"
          h={{ base: '350px', lg: '500px' }}
          objectFit="cover"
          transition="transform 0.3s ease"
          _hover={{ transform: 'scale(1.02)' }}
        />
      )}

      {/* Poll Card */}
      {post.poll && (
        <Box px={{ base: 3, lg: 4 }} py={3}>
          <PollCard poll={post.poll} urlKey={post.urlKey} />
        </Box>
      )}

      <Box p={{ base: 3, lg: 4 }} pt={{ base: 2, lg: 3 }}>
        {/* Engagement Actions */}
        <HStack spacing={{ base: 1, lg: 2 }} justify="space-around" align="flex-start">
          {/* Reaction Bar (replaces like button) */}
          <Box flex={1}>
            <ReactionBar
              totalCount={totalReactions}
              counts={reactionCounts}
              userReaction={userReaction}
              recentUsers={reactedBy}
              onReact={handleReact}
              onRemoveReaction={handleRemoveReaction}
              onShowReactions={onLikesModalOpen}
              isLoading={isReacting || isUnreacting}
            />
          </Box>

          {!initialCommentsOpen && (
            // paire-app#132: bumped to the same 44px height as the Share
            // button below so the symmetric flex-1 pair stays even — only
            // Share's icon/color changed, not its footprint.
            <Button
              leftIcon={<MessageCircle size={18} />}
              variant="ghost"
              color="var(--card-text-2)"
              _hover={{ bg: 'var(--card-surface-2)' }}
              size={{ base: 'sm', lg: 'sm' }}
              minH="44px"
              flex={1}
              onClick={handleCommentClick}
              fontSize={{ base: 'xs', lg: 'sm' }}
            >
              <Text display={{ base: 'none', lg: 'inline' }}>
                {(post.commentCount ?? 0) > 0 ? `Comment (${post.commentCount})` : 'Start the conversation!'}
              </Text>
              <Text display={{ base: 'inline', lg: 'none' }}>
                {(post.commentCount ?? 0) > 0 ? `(${post.commentCount})` : ''}
              </Text>
            </Button>
          )}
          {/* paire-app#132: modest color-token fix + size bump, not the full
              bordered treatment — this sits in a symmetric flex-1 row next
              to Comment, and bordering only Share would read as a broken
              layout rather than an intentional highlight. The icon carries
              the brand share token; label stays on --card-text-2 like
              ProfileActionBar's own text-color note (same-hue text over a
              same-hue tint can drop under 4.5:1). */}
          <Button
            leftIcon={<Share2 size={20} color="var(--color-icon-share)" />}
            variant="ghost"
            color="var(--card-text-2)"
            _hover={{ bg: 'var(--card-surface-2)' }}
            size={{ base: 'sm', lg: 'sm' }}
            minH="44px"
            flex={1}
            onClick={handleShare}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            <Text display={{ base: 'none', lg: 'inline' }}>
              {shareCount > 0 ? `Share (${shareCount})` : 'Share with friends!'}
            </Text>
            <Text display={{ base: 'inline', lg: 'none' }}>
              {shareCount > 0 ? `(${shareCount})` : ''}
            </Text>
          </Button>
        </HStack>

        {/* Comments Section */}
        <Comments
          postId={post.id}
          isOpen={commentsOpen}
          onToggle={() => setCommentsOpen(!commentsOpen)}
          initialComments={post.comments}
        />
      </Box>

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={onReportModalClose}
        contentType="post"
        contentId={post.id}
        contentDescription={`${[post.user?.firstName, post.user?.lastName].filter(Boolean).join(' ') || 'Member'}'s post`}
      />

      {/* Likes Modal */}
      <LikesModal
        isOpen={isLikesModalOpen}
        onClose={onLikesModalClose}
        users={post.likedBy || []}
      />
    </Box>
  );
})
